<script setup lang="ts">
import {reactive, ref} from "vue";
import axios from "axios";

const errorMessage = ref("")
const loggingIn = ref(false)
const sendingEmailLink = ref(false)
const form = reactive({username: "", password: ""})
const emailLogin = reactive({email: ""})

async function loginSubmit() {
  try {
    loggingIn.value = true
    const response = await axios.post("/login", {login: form.username, password: form.password})
    window.location.assign(response.data.redirect)
  } catch (error: any) {
    if (error.response?.data?.errors?.login?.[0]) {
      showError("Invalid username or password.  Please try again")
    } else {
      showError(error.toString())
    }
  } finally {
    loggingIn.value = false
    form.password = ""
  }
}

function showError(msg: string) {
  errorMessage.value = msg
}

async function emailLoginSubmit() {
  try {
    sendingEmailLink.value = true
    const email = emailLogin.email
    const response = await axios.post("/teacher/send_email_link", {email})
    const message = response.data.error || `An email was sent to ${email} with a link you can click to login.  Please check your email.`
    showError(message) // uses showError for success.  sigh.
    emailLogin.email = ""
  } catch (error: any) {
    showError(error.toString())
  } finally {
    sendingEmailLink.value = false
  }
}

defineExpose({form})

</script>

<template>
  <div class="teacher-login">
    <div class="text-center">
      <h1>Welcome back</h1>
      <p>Sign on with your Teacher or Parent email or username</p>
    </div>

    <div v-if="errorMessage" class="form-group text-center text-danger text-bold">
      {{ errorMessage }}
    </div>

    <form data-test-id="login-form" @submit.prevent="loginSubmit">
      <input
        v-model="form.username"
        type="text"
        class="form-control form-control-lg"
        placeholder="Enter username or email address"
        required
        maxLength="45"
      />

      <input
        v-model="form.password"
        type="password"
        class="js-toggle-password form-control form-control-lg"
        required
        placeholder="Enter password"
        maxLength="32"
        @keyup.enter="loginSubmit"
      />

      <button
        class="btn btn-primary btn-lg"
        type="submit"
        :disabled="loggingIn"
      >
        <span v-if="loggingIn">Logging in...</span>
        <span v-if="!loggingIn">Login to L2L</span>
      </button>
    </form>

    <SocialLoginButtons/>

    <h5>or</h5>

    <form data-test-id="send-login-link-form" @submit.prevent="emailLoginSubmit">
      <input
        v-model="emailLogin.email"
        type="email"
        class="form-control form-control-lg"
        placeholder="Login with your email address"
        required
        data-test-id="email-input"
        @keyup.enter="emailLoginSubmit"
      />

      <button class="btn btn-primary btn-lg" type="submit" :disabled="sendingEmailLink" data-test-id="send-link-button">
        <span v-if="sendingEmailLink">Sending...</span>
        <span v-if="!sendingEmailLink">Send Login Link</span>
      </button>
    </form>

    <div class="help-links">
      <a class="form-label-link" href="/password/reset">Forgot username or password?</a>
      <a class="link" href="/sign-up" title="Need An Account">Need An Account?</a>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.teacher-login {
  max-width: 28rem;
  margin: 10rem auto; // clear the menu bar and put some room before footer
}

h1, h2, h3, h4, h5, h6 {
  margin: 1.5rem auto;
  text-align: center;
}

h1 {
  font-size: 2rem;
}

form {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  & > * {
    width: 100%;
    margin: 1rem auto;
  }
}

.help-links {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}
</style>
